// Desert Vivarium / Paludarium — living ecosystem model: photoreal species billboards,
// animated animals, pool, sun shafts, day/night. Route: /vivarium.
import React, { useRef, useState, useCallback, Suspense } from "react";
import * as THREE from "three";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { EffectComposer, Bloom, Vignette, Noise } from "@react-three/postprocessing";
import { SPECIES, GROUND_SPOTS } from "../components/vivarium/data";
import {
  Enclosure, Terrain, SpeciesBillboard, TortoiseWalker, DragonflyHover,
  ChuckwallaBask, RoadrunnerStand, SunShafts, DustMotes, HydroSystem, RoomBackdrop,
} from "../components/vivarium/VivariumParts";
import { VivHeader, SpeciesIndex, SpeciesCard, VivBottomBar } from "../components/vivarium/VivariumPanels";
import { PhotoMode } from "../components/vivarium/PhotoMode";

function Grid0Ground({ day }) {
  return (
    <mesh position={[0, -3.58, 0]} rotation={[-Math.PI / 2, 0, 0]}>
      <circleGeometry args={[40, 48]} />
      <meshStandardMaterial color={day ? "#171310" : "#0a0c14"} roughness={1} />
    </mesh>
  );
}

export default function Vivarium() {
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(true);
  const [day, setDay] = useState(true);
  const [photoMode, setPhotoMode] = useState(false);
  const controlsRef = useRef();

  const select = useCallback((id) => setSelected(id), []);

  const statics = SPECIES.filter((s) => !["viv-tortoise", "viv-chuckwalla", "viv-roadrunner", "viv-dragonfly", "viv-hydro"].includes(s.id));
  const groundExtra = Object.entries(GROUND_SPOTS).flatMap(([img, spots]) =>
    spots.map((p, i) => ({ ...SPECIES.find((s) => s.id === img), id: `${img}-x${i}`, img, pos: p })));

  return (
    <div className="h-[100dvh] w-screen flex flex-col overflow-hidden bg-[#0b0a08]" data-testid="vivarium-page">
      <VivHeader day={day} onDay={() => setDay((d) => !d)} onPhoto={() => setPhotoMode(true)} />
      <div className="relative flex-1 min-h-0">
        <Canvas
          data-testid="vivarium-canvas"
          camera={{ position: [12, 5.5, 13], fov: 42 }}
          dpr={[1, 2.5]}
          gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.12 }}
          style={{ position: "absolute", inset: 0 }}
          onPointerMissed={() => setSelected(null)}
        >
          <color attach="background" args={[day ? "#14100b" : "#070912"]} />
          <fog attach="fog" args={day ? ["#14100b", 22, 48] : ["#070912", 16, 40]} />
          <ambientLight intensity={day ? 0.65 : 0.18} />
          <directionalLight position={[8, 12, 5]} intensity={day ? 1.0 : 0.12} color={day ? "#ffe3b3" : "#93a7d4"} />
          <pointLight position={[0, 2.8, 0]} intensity={day ? 12 : 3} distance={16} color={day ? "#ffd88a" : "#5b6ea6"} />
          <Grid0Ground day={day} />
          <Suspense fallback={null}>
            <RoomBackdrop day={day} />
          </Suspense>
          <Enclosure />
          <Terrain />
          <Suspense fallback={null}>
            {[...statics, ...groundExtra].map((sp) => (
              <SpeciesBillboard key={sp.id} sp={sp} running={running} selected={selected === sp.id} onSelect={select} />
            ))}
            <TortoiseWalker running={running} />
            <DragonflyHover running={running} />
            <ChuckwallaBask running={running} />
            <RoadrunnerStand running={running} />
            <HydroSystem running={running} selected={selected === "viv-hydro"} onSelect={select} />
          </Suspense>
          <SunShafts day={day} />
          <DustMotes running={running} />
          <OrbitControls ref={controlsRef} makeDefault enableDamping minDistance={4} maxDistance={30} maxPolarAngle={Math.PI / 2 + 0.05} />
          <EffectComposer>
            <Bloom intensity={0.35} luminanceThreshold={0.65} luminanceSmoothing={0.25} mipmapBlur />
            <Noise opacity={0.055} />
            <Vignette eskil={false} offset={0.22} darkness={0.62} />
          </EffectComposer>
        </Canvas>
        <SpeciesIndex selected={selected} onSelect={select} />
        <SpeciesCard selected={selected} onClose={() => setSelected(null)} />
        {photoMode && <PhotoMode onClose={() => setPhotoMode(false)} />}
        <div className={`md:hidden absolute left-2 right-2 bottom-2 gap-2 overflow-x-auto z-10 pb-1 ${selected ? "hidden" : "flex"}`} data-testid="species-chips-mobile">
          {SPECIES.filter((s) => s.id !== "viv-datepalm2").map((s) => (
            <button
              key={s.id}
              data-testid={`viv-chip-${s.id}`}
              onClick={() => select(s.id)}
              className={`px-3 py-2 rounded-lg font-mono text-[10px] tracking-widest whitespace-nowrap border min-h-[44px] ${
                selected === s.id ? "bg-amber-400 text-stone-950 border-amber-300" : "bg-stone-950/85 text-amber-200/90 border-amber-500/30"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      </div>
      <VivBottomBar running={running} onRun={() => setRunning((r) => !r)} />
    </div>
  );
}
